<template>
  <div class="text-dark report-categories">
    <div class="audit-chart">
      <div class="half">
        <strong>Primary Category</strong>
        <p class="report-categories__matched">{{ primaryCategoriesText }}</p>
        <div class="report-categories__list">
          <div
            v-for="(category, categoryName) in primaryCategories"
            :key="categoryName"
            class="report-categories__item" :class="{'report-categories__item--matched': category.matched}">
            <i v-if="category.matched" class="fa-solid fa-circle-check" />
            <span>
              {{ getFormattedPercentage(category.occurences, totalPrimaryCategories) }}% {{ categoryName }}
            </span>
          </div>
        </div>
      </div>
      <div class="half">
        <strong>Secondary Category</strong>
        <p class="report-categories__matched">{{ secondaryCategoriesText }}</p>
        <div class="report-categories__list">
          <div
            v-for="(category, categoryName) in secondaryCategories"
            :key="categoryName"
            class="report-categories__item" :class="{'report-categories__item--matched': category.matched}">
            <i v-if="category.matched" class="fa-solid fa-circle-check" />
            <span>
              {{ getFormattedPercentage(category.occurences, totalSecondaryCategories) }}% {{ categoryName }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <h2 class="h4 mb-2 font-weight-semi-bold">Competitors</h2>

    <VueSlimTable
      ref="table"
      :source="() => categoriesForAllBusinesses"
      :columns="columns"
      class="responsive-table borderless-table audit-table competitors">
      <template #row="{ row }">
        <tr>
          <td class="column" column-caption="Name">
            {{ row.name || 'My Location' }}
            <small v-if="row.formattedAddress" class="d-block">{{ row.formattedAddress }}</small>
          </td>
          <td column-caption="Primary Category">
            {{ (row.primary || {}).name }}
          </td>
          <td column-caption="Secondary Category">
            <div class="d-flex flex-wrap gap-2">
              <div v-for="{ name, remoteId } in row.additional" :key="remoteId" class="tag">
                <div class="tag-text">
                  <span>{{ name }}</span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
      <template #pagination>
        <span>&nbsp;</span>
      </template>
    </VueSlimTable>
  </div>
</template>

<script>
export default {
  props: {
    sectionData: { type: Object, required: true }
  },
  data() {
    return {
      primaryCategories: {},
      secondaryCategories: {},
      totalPrimaryCategories: 0,
      totalSecondaryCategories: 0
    }
  },
  created() {
    this.columns = [
      { key: 'name', title: 'Name', orderable: false },
      { key: 'primary', title: 'Primary Category', orderable: false },
      { key: 'additional', title: 'Secondary Category', orderable: false }
    ]

    this.categoriesForAllBusinesses = [
      {
        ...this.sectionData.current
      },
      ...this.sectionData.competitors
    ]
  },
  computed: {
    currentCategories() {
      return this.sectionData.current
    },
    primaryCategoriesText() {
      const primaryMatches = this.primaryCategories[this.currentCategories.primary.name]?.matched

      if (primaryMatches === 0) {
        return 'Your primary category does not match any other competitor'
      }

      return `Your category matches with ${primaryMatches} ${this.pluralize('competitor', primaryMatches)}.`
    },
    secondaryCategoriesText() {
      const secondaryMatches = this.currentCategories.additional
        .filter((category) => this.secondaryCategories[category.name]?.matched)
        .length

      if (secondaryMatches === 0) {
        return 'None of your secondary categories have matches in competitors locations'
      }

      const uniqueCategoriesAmount = Object.keys(this.secondaryCategories).length

      return `${secondaryMatches} out of ${
        uniqueCategoriesAmount
      } ${this.pluralize('categor', uniqueCategoriesAmount, ['y', 'ies'])} match.`
    }
  },
  mounted() {
    const primaryCategoriesMap = new Map()
    const secondaryCategoriesMap = new Map()

    const currentPrimaryCategoryName = this.currentCategories.primary.name

    if (currentPrimaryCategoryName) {
      this.totalPrimaryCategories += 1
      primaryCategoriesMap.set(currentPrimaryCategoryName, {
        occurences: 1,
        matched: 0
      })
    }

    const currentSecondaryCategoryNames = new Set(this.currentCategories.additional.map((category) => category.name))

    currentSecondaryCategoryNames.forEach((categoryName) => {
      this.totalSecondaryCategories += 1
      secondaryCategoriesMap.set(categoryName, {
        occurences: 1,
        matched: 0
      })
    })

    this.sectionData.competitors.forEach(({ primary, additional }) => {
      if (primary) {
        this.totalPrimaryCategories += 1

        const alreadyMappedCategory = primaryCategoriesMap.get(primary.name)

        if (alreadyMappedCategory) {
          alreadyMappedCategory.occurences += 1
          if (primary.name === currentPrimaryCategoryName) {
            alreadyMappedCategory.matched += 1
          }
        } else {
          primaryCategoriesMap.set(primary.name, {
            occurences: 1,
            matched: 0
          })
        }
      }

      additional.forEach((secondary) => {
        this.totalSecondaryCategories += 1

        const alreadyMappedCategory = secondaryCategoriesMap.get(secondary.name)

        if (alreadyMappedCategory) {
          alreadyMappedCategory.occurences += 1
          if (currentSecondaryCategoryNames.has(secondary.name)) {
            alreadyMappedCategory.matched += 1
          }
        } else {
          secondaryCategoriesMap.set(secondary.name, {
            occurences: 1,
            matched: 0
          })
        }
      })
    })

    this.primaryCategories = Object.fromEntries(primaryCategoriesMap)
    this.secondaryCategories = Object.fromEntries(secondaryCategoriesMap)
  },
  methods: {
    pluralize(s, num, endings = ['', 's']) {
      const [nonPluralEnding, pluralEnding] = endings
      return num === 1 ? `${s}${nonPluralEnding}` : `${s}${pluralEnding}`
    },
    getFormattedPercentage(num1, num2, precision = 1) {
      return parseFloat((num1 / num2 * 100).toFixed(precision))
    },
    matchCategories(currentCategories, competitorCategory) {
      return Boolean(currentCategories.find(({ name }) => name === competitorCategory))
    }
  }
}
</script>
